import { Fragment } from "react";
import useSWR from "swr";
import { fetcher, fmt, pairLabel } from "@/lib/api";

function cellColor(v) {
  if (v >= 0) return `rgba(246, 70, 93, ${Math.min(0.9, 0.08 + v * 0.8)})`;
  return `rgba(14, 203, 129, ${Math.min(0.9, 0.08 + Math.abs(v) * 0.8)})`;
}

export function CorrelationHeatmap({ refreshKey }) {
  const { data } = useSWR(`/portfolio/correlation?tf=1h&bars=200${refreshKey ? `&k=${refreshKey}` : ""}`, fetcher, { refreshInterval: 60000 });
  if (!data) return <div className="panel p-3 text-xs text-[#5e6673]">Computing correlations…</div>;
  const { symbols, matrix, held, exposure_pct, side } = data;
  const short = (s) => pairLabel(s);
  const lvl = data.concentration_level;
  const lvlCls = lvl === "high" ? "text-[#f6465d]" : lvl === "moderate" ? "text-[#fcd535]" : lvl === "low" ? "text-[#0ecb81]" : "text-[#848e9c]";
  return (
    <div className="panel p-3" data-testid="correlation-heatmap">
      <div className="flex items-center justify-between mb-2">
        <div className="panel-title">Correlation Heatmap · 1h log returns</div>
        <span className={`pill bg-[#1e2329] ${lvlCls}`} data-testid="concentration-level">{lvl === "none" ? "NO POSITIONS" : `${lvl.toUpperCase()} CONCENTRATION`}</span>
      </div>
      <div className="grid gap-[2px] mono text-[10px]" style={{ gridTemplateColumns: `44px repeat(${symbols.length}, minmax(0, 1fr))` }}>
        <div />
        {symbols.map((s) => <div key={s} className={`text-center truncate ${held.includes(s) ? "text-white font-semibold" : "text-[#5e6673]"}`}>{short(s)}</div>)}
        {symbols.map((row, i) => (
          <Fragment key={row}>
            <div className={`flex items-center ${held.includes(row) ? "text-white font-semibold" : "text-[#5e6673]"}`}>
              {short(row)}{side[row] && <span className={`ml-1 text-[8px] ${side[row] === "LONG" ? "up" : "down"}`}>{side[row][0]}</span>}
            </div>
            {symbols.map((col, j) => {
              const v = matrix[i][j];
              const both = held.includes(row) && held.includes(col) && i !== j;
              return (
                <div key={`${row}-${col}`} className={`h-8 flex items-center justify-center rounded-[2px] ${both && v > 0.7 ? "ring-1 ring-[#f6465d]" : ""}`} style={{ background: i === j ? "#2b3139" : cellColor(v) }}
                  title={`${short(row)} / ${short(col)}: ${v}`} data-testid={`corr-${short(row)}-${short(col)}`}>
                  <span className={i === j ? "text-[#5e6673]" : "text-white"}>{i === j ? "—" : v.toFixed(2)}</span>
                </div>
              );
            })}
          </Fragment>
        ))}
      </div>
      <div className="mt-2 space-y-1 text-[11px]">
        <div className="flex justify-between"><span className="text-[#848e9c]">Held positions</span><span className="mono text-white">{held.length ? held.map((s) => `${short(s)} ${fmt(exposure_pct[s], 1)}%`).join(" · ") : "—"}</span></div>
        <div className="flex justify-between"><span className="text-[#848e9c]">Avg correlation (held)</span><span className="mono text-white">{data.avg_held_correlation ?? "—"}</span></div>
        <div className="flex justify-between"><span className="text-[#848e9c]">Corr-weighted exposure</span><span className={`mono ${data.correlation_weighted_exposure_pct > data.limit_correlated_exposure_pct ? "down" : "text-white"}`}>{fmt(data.correlation_weighted_exposure_pct, 1)}% <span className="text-[#5e6673]">/ {data.limit_correlated_exposure_pct}% limit</span></span></div>
        {Object.keys(data.by_sector_pct).length > 0 && <div className="flex justify-between"><span className="text-[#848e9c]">By sector</span><span className="mono text-white">{Object.entries(data.by_sector_pct).map(([k, v]) => `${k} ${fmt(v, 1)}%`).join(" · ")}</span></div>}
        {data.concentrated_pairs.map((p) => (
          <div key={`${p.a}${p.b}`} className="text-[#f6465d] bg-[rgba(246,70,93,0.08)] rounded px-2 py-1" data-testid="concentrated-pair-warning">⚠ {short(p.a)} & {short(p.b)} move together (ρ {p.correlation}) — combined {fmt(p.combined_exposure_pct, 1)}% exposure acts as one bet</div>
        ))}
      </div>
    </div>
  );
}
